/**
 * Wholesalers solution page.
 */
import { Users2, Target, Brain, LineChart, Building2, Landmark } from "lucide-react";
import SolutionPageTemplate from "../SolutionPageTemplate";

export default function WholesalersSolutionPage() {
  return (
    <SolutionPageTemplate
      slug="wholesalers"
      persona="solo wholesalers & acquisition teams"
      eyebrow="Solutions · Wholesalers"
      title="Analyze faster."
      titleAccent="Assign more contracts."
      subtitle="Cut analysis time from 90 minutes to 6. Every property is scored, matched to verified buyers, and assigned with a numeric contract price — before the seller changes their mind."
      bullets={[
        "1-tap Deal Score (BP-005) with cited comps and repair evidence",
        "Top-3 verified buyers ranked by strategy + funding, per property",
        "Contract, e-sign, and assignment fee tracked on the immutable timeline",
      ]}
      metricStrip={[
        { stat: "6 min", label: "avg. underwriting time" },
        { stat: "3.4×", label: "faster to buyer signature" },
        { stat: "8", label: "exit strategies scored" },
        { stat: "$0", label: "cost of a bad call" },
      ]}
      pains={[
        { title: "Analysis takes forever", desc: "Comps in one tab, repairs in another, ARV in a third — 90 minutes gone before you can offer." },
        { title: "Buyers ghost you", desc: "You blast the list, three respond, none close. Because most aren't funded or fit the deal." },
        { title: "Strategy debates", desc: "'Should we novate instead?' Endless meetings, no numbers. Deals die in the debate." },
        { title: "Missing the seller's window", desc: "By the time you have a number, the seller has three other offers on the table." },
      ]}
      playbook={[
        { step: 1, title: "Ingest & score", desc: "Address in → Deal Score, ARV, Repair, Market Tier out. All cited.", bp: "BP-005 · BP-004 · BP-003" },
        { step: 2, title: "Compare exits", desc: "Wholesale, Novation, Subject-To, Wholetail scored side-by-side with Why-Not audit.", bp: "BP-014" },
        { step: 3, title: "Match buyers", desc: "Top-3 verified funded buyers surface automatically, ranked by fit.", bp: "BP-022 · BP-018" },
        { step: 4, title: "Assign & close", desc: "Contract, e-sign, wire — all on the immutable timeline.", bp: "BP-002 · BP-013" },
      ]}
      productsUsed={[
        { icon: Target, title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Wholesale vs Novation vs Wholetail — decided in seconds, not meetings." },
        { icon: Users2, title: "Buyer Intelligence", to: "/products/buyer-intelligence", desc: "Ranked, verified buyers with proof of funds — top 3 in one click." },
        { icon: Brain, title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Grounded next-best-action on every lead — never invents a number." },
        { icon: Building2, title: "Property Workspace", to: "/products/property-workspace", desc: "Every lead becomes a command center with a full timeline." },
        { icon: LineChart, title: "Market Intelligence", to: "/products/market-intelligence", desc: "Only chase ZIPs where wholesale is Hot — spot inflections early." },
        { icon: Landmark, title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Close rate, assignment fee, source ROI — six-dimension health." },
      ]}
      proof={{
        stat: "42",
        label: "assignments in 12 months (solo)",
        quote: "I cut underwriting from 90 minutes to 6 and stopped losing sellers to slow numbers. The top-3 buyer matches close in half the time.",
        attribution: "Solo wholesaler · Cleveland",
      }}
      related={[
        { title: "Fix & Flip", to: "/solutions/fix-and-flip", desc: "For when you want to keep the deal instead of assigning." },
        { title: "Creative Finance", to: "/solutions/creative-finance", desc: "Subject-To and Seller-Finance moves for deeper margins." },
        { title: "Teams & Enterprise", to: "/solutions/teams-enterprise", desc: "Scale your wholesale operation with governance." },
      ]}
    />
  );
}
